import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Text,
  Avatar,
  Tag,
  Link,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { instagramProfileDetail } from "../../api";

const INSTAGRAM_BASE = "https://www.instagram.com";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "-");

export function InstagramProfileDetailModal({ isOpen, onClose, profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!profileId || !isOpen) return;
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, isOpen]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await instagramProfileDetail(profileId);
      setData(res?.data);
    } catch (e) {
      console.error("인스타그램 프로필 상세 조회 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "lg" }} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={{ base: 0, md: "20px" }} p={{ base: "16px", md: "10px" }} maxH="90vh" mx={{ base: 0, md: 4 }}>
        <ModalHeader fontSize={{ base: "lg", md: "22px" }}>인스타그램 프로필 상세</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb="30px">
          {loading ? (
            <Flex justify="center" py="60px">
              <Spinner size="lg" />
            </Flex>
          ) : data ? (
            <Box display="flex" flexDir="column" gap="24px">
              {/* 프로필 헤더 */}
              <Flex align="center" gap={{ base: "12px", md: "16px" }} flexWrap="wrap">
                <Avatar
                  name={data.username}
                  src={data.profilePicUrl || ""}
                  size={{ base: "lg", md: "xl" }}
                />
                <Box minW="0" flex="1">
                  <Flex align="center" gap="8px" mb="4px" flexWrap="wrap">
                    <Text fontWeight="700" fontSize={{ base: "16px", md: "20px" }} noOfLines={1}>
                      @{data.username}
                    </Text>
                    {data.isVerified && (
                      <Tag size="sm" colorScheme="blue">
                        인증
                      </Tag>
                    )}
                    {data.isBusiness && (
                      <Tag size="sm" colorScheme="purple">
                        비즈니스
                      </Tag>
                    )}
                  </Flex>
                  {data.fullName && (
                    <Text color="gray.600" fontSize="14px" mb="8px">
                      {data.fullName}
                    </Text>
                  )}
                  <Link
                    href={`${INSTAGRAM_BASE}/${data.username}`}
                    isExternal
                    color="#0054C0"
                    fontSize="14px"
                  >
                    인스타그램에서 보기 →
                  </Link>
                </Box>
              </Flex>

              {/* 연락처 */}
              {data.contactEmail && (
                <Box>
                  <Text fontSize="12px" color="gray.500" mb="4px">
                    연락 이메일
                  </Text>
                  <Text fontSize="16px" fontWeight={500}>
                    {data.contactEmail}
                  </Text>
                </Box>
              )}

              {/* 자기소개 */}
              {data.biography && (
                <Box>
                  <Text fontSize="12px" color="gray.500" mb="4px">
                    자기소개
                  </Text>
                  <Text fontSize="14px" whiteSpace="pre-wrap">
                    {data.biography}
                  </Text>
                </Box>
              )}

              {/* 수치 정보 */}
              <Flex gap={{ base: "16px", md: "24px" }} flexWrap="wrap">
                <Box>
                  <Text fontSize="12px" color="gray.500">
                    팔로워
                  </Text>
                  <Text fontWeight="700" fontSize={{ base: "16px", md: "18px" }}>
                    {fmt(data.followerCount)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="12px" color="gray.500">
                    팔로잉
                  </Text>
                  <Text fontWeight="700" fontSize={{ base: "16px", md: "18px" }}>
                    {fmt(data.followingCount)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="12px" color="gray.500">
                    게시물
                  </Text>
                  <Text fontWeight="700" fontSize={{ base: "16px", md: "18px" }}>
                    {fmt(data.postCount)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="12px" color="gray.500">
                    평균 좋아요
                  </Text>
                  <Text fontWeight="700" fontSize={{ base: "16px", md: "18px" }}>
                    {fmt(data.avgLikes)}
                  </Text>
                </Box>
              </Flex>

              {/* 계정 상태 */}
              {data.accountStatus && (
                <Box>
                  <Text fontSize="12px" color="gray.500" mb="4px">
                    계정 상태
                  </Text>
                  <Tag>{data.accountStatus}</Tag>
                </Box>
              )}

              {/* 태그 */}
              {data.tags?.length > 0 && (
                <Box>
                  <Text fontSize="12px" color="gray.500" mb="8px">
                    태그
                  </Text>
                  <Flex gap="8px" flexWrap="wrap">
                    {data.tags.map((t) => (
                      <Tag key={t.id} size="md" colorScheme="blue" variant="subtle">
                        #{t.tagName}
                      </Tag>
                    ))}
                  </Flex>
                </Box>
              )}

              {/* 수집 시각 */}
              <Box pt="16px" borderTop="1px solid" borderColor="gray.200">
                <Text fontSize="12px" color="gray.500">
                  수집: {data.scrapedAt ? new Date(data.scrapedAt).toLocaleString("ko-KR") : "-"} / 수정:{" "}
                  {data.updatedAt ? new Date(data.updatedAt).toLocaleString("ko-KR") : "-"}
                </Text>
              </Box>
            </Box>
          ) : (
            <Text color="gray.500" textAlign="center" py="40px">
              프로필 정보를 불러올 수 없습니다.
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
